import { z } from "zod"

import { TenantIdSchema } from "./auth.js"
import {
	CountryCodeSchema,
	HsCodeSchema,
	LotIdSchema,
	ShipmentIdSchema,
	VerticalIdSchema,
	WarehouseIdSchema,
} from "./id-schemas.js"

/**
 * Lot — RAM-OS's NSB##-numbered shipment unit.
 *
 * A Shipment has one or more Lots; each Lot is tied to a Vertical
 * (Cashew / Soybean / future) and an origin country. HS code + packing
 * spec default from the Vertical record but can be overridden per Lot.
 * `shipmentId` is optional so a Lot can exist before being assigned to a
 * booking (e.g. produced from a warehouse, awaiting carrier).
 */

const Bounded = (max = 256) => z.string().min(1).max(max)
const IsoDateTime = z.string().datetime()

export const LotUnitSchema = z.enum(["MT", "KG", "BAGS", "CTNS", "BOXES"])
export type LotUnit = z.infer<typeof LotUnitSchema>

export const LotSchema = z.object({
	id: LotIdSchema,
	tenantId: TenantIdSchema,
	nsbNumber: Bounded(64),
	verticalId: VerticalIdSchema,
	// Joined vertical display name surfaced by the read endpoints. Nullable
	// (left join) + optional (not all producers populate it).
	verticalName: Bounded(128).nullable().optional(),
	originCountry: CountryCodeSchema,
	originWarehouseId: WarehouseIdSchema.optional(),
	shipmentId: ShipmentIdSchema.optional(),
	hsCode: HsCodeSchema.optional(),
	packingSpec: z.string().max(512).optional(),
	quantity: z.number().nonnegative(),
	unit: LotUnitSchema,
	createdAt: IsoDateTime,
	updatedAt: IsoDateTime,
})
export type Lot = z.infer<typeof LotSchema>
